/**
 * ColorNode - Composes RGBA color from individual components.
 *
 * Supports RGB and HSL modes. When any component has useExp (getter function),
 * composes a live getter that evaluates per-call via endpointApi — no per-frame
 * store writes needed.
 */

import { BaseNode } from '@nodes/BaseNode';
import { params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import { hslToRgba, type RGBA } from '../../threejs/colorUtils';

const colorEndpoints = params({
  colorOut: { dataType: 'color', isOutput: true, primary: true, label: 'Color' },
  mode: { dataType: 'string', value: 'rgb', options: ['rgb', 'hsl'] },
  r: { value: 0.5, min: 0, max: 1, step: 0.01, group: 'RGB' },
  g: { value: 0.5, min: 0, max: 1, step: 0.01, group: 'RGB' },
  b: { value: 0.5, min: 0, max: 1, step: 0.01, group: 'RGB' },
  h: { value: 0.5, min: 0, max: 1, step: 0.01, group: 'HSL' },
  s: { value: 0.5, min: 0, max: 1, step: 0.01, group: 'HSL' },
  l: { value: 0.5, min: 0, max: 1, step: 0.01, group: 'HSL' },
  a: { value: 1, min: 0, max: 1, step: 0.01 },
});

class ColorNode extends BaseNode<typeof colorEndpoints> {
  private _suppressEmit = false;

  initialize() {
    super.initialize();
    this.emitColor();
  }

  onParamChanged(_payload: any, endpointId: string) {
    if (this._suppressEmit) return;

    if (endpointId === 'colorOut') {
      // User picked a color on the swatch — decompose to components
      this.decomposeColor(this.params.colorOut.value as RGBA);
      return;
    }
    // Component changed — recompose
    this.emitColor();
  }

  private decomposeColor(rgba: RGBA) {
    this._suppressEmit = true;
    // Skip writing to endpoints that have getters — their getter controls the value,
    // writing to them would create a feedback loop (getter reads store → decomposeColor writes store)
    const api = this.deps.endpointApi;
    if (!api.hasGetter('r')) this.params.r.set(rgba.r);
    if (!api.hasGetter('g')) this.params.g.set(rgba.g);
    if (!api.hasGetter('b')) this.params.b.set(rgba.b);
    if (!api.hasGetter('a')) this.params.a.set(rgba.a);
    this._suppressEmit = false;
    // Re-emit so colorOut getter is updated
    this.emitColor();
  }

  private emitColor() {
    const color = this.composeColor();
    const endpointApi = this.deps.endpointApi;

    if (this.hasFunctionParams()) {
      // Compose a factory that evaluates component getters per-call
      const composedFactory = (_ctx: Record<string, any>) => (_dt: number) => this.composeColorFromApi();
      endpointApi.setGetFactory('colorOut', composedFactory);
    } else {
      endpointApi.setGetFactory('colorOut', null);
    }

    this.params.colorOut.set(color);
  }

  private composeColor(): RGBA {
    const mode = this.params.mode.value;
    if (mode === 'hsl') {
      return hslToRgba(this.params.h.value, this.params.s.value, this.params.l.value, this.params.a.value);
    }
    return { r: this.params.r.value, g: this.params.g.value, b: this.params.b.value, a: this.params.a.value };
  }

  private composeColorFromApi(): RGBA {
    const endpointApi = this.deps.endpointApi;
    const mode = endpointApi.getValue('mode');
    if (mode === 'hsl') {
      return hslToRgba(endpointApi.getValue('h'), endpointApi.getValue('s'), endpointApi.getValue('l'), endpointApi.getValue('a'));
    }
    return { r: endpointApi.getValue('r'), g: endpointApi.getValue('g'), b: endpointApi.getValue('b'), a: endpointApi.getValue('a') };
  }
}

export const colorNodeDef: NodeDef<ColorNode> = {
  type: 'Color',
  shortName: 'color',
  info: 'Composes RGBA color from individual components. Supports RGB and HSL modes.',
  category: 'generator',
  endpoints: colorEndpoints,
  ctor: ColorNode
};
